import { createContext, useCallback, useContext, useState } from "react";

export type EntityType =
  | "part" | "bom" | "change" | "cae"
  | "drawing" | "knowledge" | "action" | "search" | "today";

export interface FocusedEntity {
  type: EntityType;
  id: string;
  label: string; // shown in tab strip
}

interface CompanionState {
  tabs: FocusedEntity[];
  activeId: string | null; // null = Today
  focus: (e: FocusedEntity) => void;
  close: (id: string) => void;
  goHome: () => void;
  setActive: (id: string | null) => void;
}

const Ctx = createContext<CompanionState | null>(null);

export function CompanionProvider({ children }: { children: React.ReactNode }) {
  const [tabs, setTabs] = useState<FocusedEntity[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  const focus = useCallback((e: FocusedEntity) => {
    setTabs((prev) => (prev.some((t) => t.id === e.id) ? prev : [...prev, e]));
    setActiveId(e.id);
  }, []);

  const close = useCallback((id: string) => {
    setTabs((prev) => {
      const next = prev.filter((t) => t.id !== id);
      setActiveId((curr) => {
        if (curr !== id) return curr;
        return next.length ? next[next.length - 1].id : null;
      });
      return next;
    });
  }, []);

  const goHome = useCallback(() => setActiveId(null), []);
  const setActive = useCallback((id: string | null) => setActiveId(id), []);

  return (
    <Ctx.Provider value={{ tabs, activeId, focus, close, goHome, setActive }}>
      {children}
    </Ctx.Provider>
  );
}

export function useCompanion() {
  const v = useContext(Ctx);
  if (!v) throw new Error("CompanionProvider missing");
  return v;
}
